import { useRef, useState } from 'react'
import type ReactECharts from 'echarts-for-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useEconomicAreaData } from '../../../hooks/useEconomicAreaData'
import EconomicAreaChart from '../../../chart/EconomicAreaChart'
import { RiskProfileCard } from '../../RiskProfileCard'
import { ChartQueryState } from '../../ChartQueryState'
import { ChartExportActions } from '../../../chart/ChartExportActions'
import { useExportXlsx } from '../../../hooks/useExportXlsx'
import { useExportChartImage } from '../../../hooks/useExportChartImage'
import type {
  PropertiesQueryParams,
  PropertiesMetricOption,
} from '../../../types/riskProfileProperties.types'

export interface EconomicAreaWidgetProps {
  queryParams: PropertiesQueryParams | null
  metricOption: PropertiesMetricOption
  entityNoun: string
  selectedAreaTypesLabel: string
}

export function EconomicAreaWidget({
  queryParams,
  metricOption,
  entityNoun,
  selectedAreaTypesLabel,
}: EconomicAreaWidgetProps) {
  const [rankBy, setRankBy] = useState<'percent' | 'count'>('percent')
  const { request, query } = useEconomicAreaData(queryParams)
  const { exportXlsx, isExporting } = useExportXlsx(request, '/report/risk-profile/properties/xlsx')
  const chartRef = useRef<ReactECharts>(null)
  const handleExportImage = useExportChartImage(chartRef, `${entityNoun}-disrupted-by-area`)

  return (
    <RiskProfileCard
      title={`${selectedAreaTypesLabel} ranked by ${entityNoun} disrupted`}
      onExport={
        <ChartExportActions
          onExportXlsx={exportXlsx}
          onExportImage={handleExportImage}
          isExportingXlsx={isExporting}
          xlsxDisabled={!request}
        />
      }
      className="min-h-96"
    >
      <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
        <span>Rank by</span>
        <Tabs value={rankBy} onValueChange={(value) => setRankBy(value as 'percent' | 'count')}>
          <TabsList className="h-7 p-0.5">
            <TabsTrigger value="percent" className="h-6 px-2 text-xs">
              % disrupted
            </TabsTrigger>
            <TabsTrigger value="count" className="h-6 px-2 text-xs">
              # disrupted
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <ChartQueryState query={query}>
        {(data) => (
          <EconomicAreaChart
            ref={chartRef}
            data={data}
            metricOption={metricOption}
            entityNoun={entityNoun}
            rankBy={rankBy}
          />
        )}
      </ChartQueryState>
    </RiskProfileCard>
  )
}
